// Product Scene Variations' form (live's `ProductSceneVariationsForm`, spec G14-templates): the
// product picture and how many variations; the server's director writes the shots, then one
// tile a shot, on live's fixed models.
import { Group, Stack, Text } from "@mantine/core";
import { Layers2 } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { ImageTemplateQuote, ImageTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import {
  exampleImage,
  ImageSlot,
  ReuseSwitch,
  slotSource,
  StartArrow,
  type FormExample,
  type SlotImage,
  type TemplateFormProps,
} from "./kit";
import { CostChip } from "../TemplateCost";

/** Live's edit model (`TWEAK_IMAGE_MODEL_ID`), by our catalog id, and its words when it is gone. */
const EDIT_MODEL = "gemini-3.1-flash-image";
const EDIT_MISSING = "The image model GEMINI_3.1 is not available.";
/** Live's counts, 1 to 10, 5 by default (`VARIATION_COUNTS`). */
const COUNTS = Array.from({ length: 10 }, (_, at) => String(at + 1));
const DEFAULT_COUNT = "5";

interface Values {
  image: SlotImage | null;
  count: string;
}

/** The form's values: the card's example's (its packshot and live's count), or empty. */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "product-scene-variations") return null;
  return { image: exampleImage(example, inputs.image), count: String(inputs.count ?? DEFAULT_COUNT) };
}

/**
 * Live's form: "Reuse example" (on fills the card's example, off clears the form; Replicate opens
 * it on), the "Image" slot ("Pick the product image to re-stage"), and in the footer the count and
 * the round start, off until there is a picture and while live's edit model is not offered.
 */
export function SceneVariationsForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [image, setImage] = useState<SlotImage | null>(start?.image ?? null);
  const [count, setCount] = useState(start?.count ?? DEFAULT_COUNT);
  const fill = (on: boolean) => {
    const values = on ? valuesOf(example) : null;
    setReuse(values !== null);
    setImage(values?.image ?? null);
    setCount(values?.count ?? DEFAULT_COUNT);
  };
  const catalog = useGetCatalog().data;
  const missing = catalog !== undefined && !catalog.image_models.some((each) => each.id === EDIT_MODEL);
  const inputs =
    image === null
      ? null
      : {
          template: "product-scene-variations" as const,
          image: slotSource(image),
          count: Number(count),
        };
  const body = inputs === null ? null : { type: "image.template" as const, inputs };
  const request = ImageTemplateRequest.safeParse(body);
  const quote = ImageTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success && !missing ? quote.data : undefined,
    request: request.success && !missing ? request.data : undefined,
    onStarted,
  });
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} />}
      summary={<Text size="sm">Product Scene Variations</Text>}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Choice
            variant="footer"
            setting="Variations"
            icon={<Layers2 size={16} strokeWidth={1.5} />}
            tip="How many scene variations to generate, from 1 to 10."
            width={65}
            value={count}
            options={COUNTS.map((each) => ({ value: each, label: each }))}
            onChange={setCount}
          />
          <Group gap={8} wrap="nowrap">
            <CostChip estimate={props.template.estimate} outputs={Number(count)} />
            <StartArrow canStart={job.canStart} starting={job.start.isPending} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <ImageSlot
          teamId={teamId}
          brandId={brandId}
          label="Image"
          title="Pick the product image to re-stage"
          tooltip="Your product image. Upload one or select it from your library."
          value={image}
          onChange={setImage}
        />
        {missing ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {EDIT_MISSING}
          </Text>
        ) : null}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts). */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started
          </Text>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}
